import {
  BackrsUser,
  CareerPathResponse,
  Coach,
  College,
  ContentCompetency,
  ContentNugget,
  DeepDive,
  Employer,
  ExternalProfile,
  GeneratedContent,
  Industry,
  Internship,
  Scholarship,
  Tool,
  TrackAssessmentResult,
  TrackAssessmentUnlock,
  EducationalInvestment,
  CareerTrack,
  Major,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { CareerUserResponseSet } from '@backrs_ui/grow-ui/src/models/models';
import type { NoteAttachment } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';

export const PRIORITY_TYPES = {
  1: 'Low',
  2: 'Medium',
  3: 'High',
} as const;

export const PRIORITY_TASK_STATUS_CODES = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
} as const;

export type TaskStatusCode =
  (typeof PRIORITY_TASK_STATUS_CODES)[keyof typeof PRIORITY_TASK_STATUS_CODES];

export type PriorityTaskStatusType = Readonly<{
  id: number;
  display_name: string;
  code: TaskStatusCode;
  sequence: number;
}>;

export const PRIORITY_TASK_TYPE_CODES = {
  SMART_GOAL: 'smart_goal',
  ADMINISTRATIVE: 'administrative',
  USE_A_TOOL: 'use_a_tool',
  EXPLORE: 'explore',
  PURSUE_OPPORTUNITY: 'pursue_opportunity',
  ENRICHMENT: 'enrichment',
  CONNECT: 'connect',
  PERSONAL: 'personal',
  OTHER: 'other',
} as const;

export type TaskTypeCode = (typeof PRIORITY_TASK_TYPE_CODES)[keyof typeof PRIORITY_TASK_TYPE_CODES];

export type PriorityTaskType = Readonly<{
  id: number;
  display_name: string;
  code: TaskTypeCode;
  content_type: string;
  is_user_selectable: boolean;
}>;

export type PriorityTask = Readonly<{
  id: number;
  title: string;
  description: string | null;
  priority: keyof typeof PRIORITY_TYPES | null;
  due_date: string | null;
  content_url: string | null;
  content_id: number | string | null;
  status_code: TaskStatusCode | null;
  type_code: TaskTypeCode | null;

  user_id: number | null;
  user?: BackrsUser;

  assigned_by_id: number | null;
  assignedBy?: BackrsUser;

  order: number | null;

  playlist_id: number | null;
  playlist?: Readonly<{
    id: number;
    title: string;
    description: string | null;
    created_at: string;
    updated_at: string;
  }>;

  tags?: Readonly<{
    id: number;
    title: string;
  }>[];

  contentNuggets?: ContentNugget[];
  scholarships?: Scholarship[];
  careerPaths?: CareerPathResponse[];
  careerTracks?: CareerTrack[];
  profiles?: BackrsUser[];
  externalProfiles?: ExternalProfile[];
  internships?: Internship[];
  academicProfiles?: College[];
  employers?: Employer[];
  generatedContent?: GeneratedContent[];
  trackAssessmentResults?: TrackAssessmentResult[];
  trackAssessmentUnlocks?: TrackAssessmentUnlock[];
  educationalInvestments?: EducationalInvestment[];
  competencies?: ContentCompetency[];
  tools?: Tool[];
  majors?: Major[];
  industries?: Industry[];
  coaches?: Coach[];
  deepDives?: DeepDive[];
  blueprints?: Pick<CareerUserResponseSet, 'id'>[];

  created_at: string;
  updated_at: string;
}>;

/**
 * this should be in sync with the backend priorities_tasks file
 */
export const TASK_CONTENT_CODES = {
  contentNuggets: 'contentNuggets',
  scholarships: 'scholarships',
  careerPaths: 'careerPaths',
  careerTracks: 'careerTracks',
  profiles: 'profiles',
  externalProfiles: 'externalProfiles',
  internships: 'internships',
  academicProfiles: 'academicProfiles',
  generatedContent: 'generatedContent',
  trackAssessmentResults: 'trackAssessmentResults',
  trackAssessmentUnlocks: 'trackAssessmentUnlocks',
  educationalInvestments: 'educationalInvestments',
  employers: 'employers',
  competencies: 'competencies',
  tools: 'tools',
  majors: 'majors',
  industries: 'industries',
  coaches: 'coaches',
  deepDives: 'deepDives',
  blueprints: 'blueprints',
} as const;

export type TaskContentCode = (typeof TASK_CONTENT_CODES)[keyof typeof TASK_CONTENT_CODES];

export type PriorityTaskPost = Omit<
  PriorityTask,
  'id' | 'user_id' | 'assigned_by_id' | 'created_at' | 'updated_at' | 'tags'
> & {
  tags: string[];
  content_code?: TaskContentCode;
};

export type PriorityTaskReflection = Readonly<{
  id: number;
  task_id: number;
  user_id: number;
  /** Lexical `EditorState.toJSON()` output. */
  body: Record<string, unknown>;
  body_plaintext: string;
  attachments: NoteAttachment[];
  created_at: string;
  updated_at: string;
}>;

export type PriorityTaskReflectionPut = Readonly<{
  taskId: number;
  body: PriorityTaskReflection['body'];
  body_plaintext: string;
  attachment_ids: number[];
}>;

export type PriorityPlaylistPost = Omit<
  PriorityPlaylist,
  | 'id'
  | 'user_id'
  | 'created_by'
  | 'created_at'
  | 'updated_at'
  | 'total_tasks'
  | 'completed_tasks'
  | 'tags'
  | 'preview_media_id'
> & {
  tags: string[];
  preview_media_id?: number | null;
};
